import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import SupportPage from "./components/SupportPage";
import axios from "axios";
import AuthPage from "./components/AuthPage";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import VerifyEmail from "./components/VerifyEmail";
import PayPalSuccess from "./components/PayPalSuccess";
import CRMDashboard from "./components/CRMDashboard";
import SubscriptionRequired from "./components/SubscriptionRequired";
import SubscriptionSuccess from "./components/SubscriptionSuccess";
import { Toaster } from "./components/ui/sonner";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

/**
 * Attach token automatically (if exists)
 * NOTE: This runs once at module load, which is good.
 */
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Handle subscription-required globally
 */
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 402) {
      window.location.href = "/subscription-required";
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!BACKEND_URL) {
      console.warn("REACT_APP_BACKEND_URL is missing. Set it in Cloudflare Pages env vars.");
    }

    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      return null;
    }

    try {
      const res = await axios.get(`${API}/auth/me`);
      setIsAuthenticated(true);
      setUser(res.data);
      // Optional: keep latest user snapshot
      localStorage.setItem("user", JSON.stringify(res.data));
      return res.data;
    } catch (err) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setIsAuthenticated(false);
      setUser(null);
      return null;
    }
  }, []);

  const loadSession = useCallback(async () => {
    setLoading(true);
    await refreshMe();
    setLoading(false);
  }, [refreshMe]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "token") {
        loadSession();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [loadSession]);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="text-[#1A4D2E] text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/crm" replace />
              ) : (
                <AuthPage setIsAuthenticated={setIsAuthenticated} setUser={setUser} />
              )
            }
          />

          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* PayPal return URL: after capture, refreshMe() so user becomes active in UI */}
          <Route
            path="/paypal/success"
            element={
              isAuthenticated ? (
                <PayPalSuccess onPaymentConfirmed={refreshMe} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path="/crm"
            element={
              isAuthenticated ? (
                <CRMDashboard setIsAuthenticated={setIsAuthenticated} user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route path="/support" element={<SupportPage />} />

          <Route
            path="/subscription-required"
            element={isAuthenticated ? <SubscriptionRequired user={user} /> : <Navigate to="/" replace />}
          />

          <Route
            path="/subscription/success"
            element={isAuthenticated ? <SubscriptionSuccess /> : <Navigate to="/" replace />}
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>

      <Toaster position="top-right" />
    </div>
  );
}

export default App;
